import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config";
import { KpiIcon } from "../../Quantative/component/KpiIcons";
import TabularDynamicForm from "./TabularDynamicForm";
import { normalizeAnswers, conformGridToQuestion } from "../services/answerShape";
import { getAllUsers } from "../../../Training/training-dashboard/services/trainingService";

/* tolerant user-name resolver — backend user objects vary in casing */
const userDisplayName = (u) => {
    if (!u || typeof u !== "object") return "";
    const first = u.firstName ?? u.first_name ?? "";
    const last = u.lastName ?? u.last_name ?? u.lastLame ?? "";
    return `${first} ${last}`.trim() || u.email || "";
};

/* prefer name fields on the entry itself (once the API joins riu_users),
   else resolve the entry's userId against the company user list */
const entryUserName = (entry, usersById = {}) => {
    const inline =
        userDisplayName(entry.user || entry.createdBy || entry.created_by_user) ||
        `${entry.firstName ?? entry.first_name ?? ""} ${entry.lastName ?? entry.last_name ?? ""}`.trim() ||
        entry.userName || entry.user_name || "";
    if (inline) return inline;
    const uid = entry.userId ?? entry.user_id ?? entry.created_by;
    return uid != null ? userDisplayName(usersById[String(uid)]) : "";
};

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmt = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
    });
};

const STATUS_MAP = {
    SUBMITTED: { color: "#16a34a", bg: "#dcfce7", label: "Submitted" },
    APPROVED:  { color: "#2563eb", bg: "#dbeafe", label: "Approved"  },
    REJECTED:  { color: "#dc2626", bg: "#fee2e2", label: "Rejected"  },
    PENDING:   { color: "#d97706", bg: "#fef3c7", label: "Pending"   },
};

const StatusBadge = ({ status }) => {
    const s = STATUS_MAP[status] || { color: "#64748b", bg: "#f1f5f9", label: status || "—" };
    return (
        <span style={{
            background: s.bg, color: s.color,
            borderRadius: 20, padding: "2px 9px",
            fontSize: 10.5, fontWeight: 700, letterSpacing: "0.03em",
            fontFamily: "var(--font-mono)",
        }}>
            {s.label}
        </span>
    );
};

// ─── Single collapsible history entry ────────────────────────────────────────
const HistoryEntry = ({ entry, question, index, isLast, usersById }) => {
    const [open, setOpen] = useState(false);
    const userName = entryUserName(entry, usersById);

    const gridData = (() => {
        if (!entry.answer) return null;
        try {
            const raw = entry.answer;
            const p = Array.isArray(raw) ? raw : JSON.parse(raw);
            if (!Array.isArray(p)) return null;
            return conformGridToQuestion(p, question?.details, entry?.columnGroups);
        } catch { return null; }
    })();

    const isCurrent = !!entry._isCurrent;
    const isFirst = index === 0;

    return (
        <div style={{ position: "relative" }}>
            {/* Timeline connector line */}
            {!isLast && (
                <div style={{
                    position: "absolute", left: 19, top: 40, bottom: 0,
                    width: 2, background: "var(--rule)", zIndex: 0,
                }} />
            )}

            {/* Entry header row — click to expand */}
            <div
                onClick={() => setOpen(p => !p)}
                style={{
                    display: "flex", alignItems: "flex-start", gap: 14,
                    cursor: "pointer", padding: "10px 0",
                    userSelect: "none", position: "relative", zIndex: 1,
                }}
            >
                {/* Timeline dot */}
                <div style={{
                    width: 20, height: 20, minWidth: 20, borderRadius: "50%",
                    background: isFirst ? "var(--accent)" : "#e2e8f0",
                    border: `2px solid ${isFirst ? "var(--accent-2)" : "#cbd5e1"}`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    marginTop: 2, flexShrink: 0,
                }}>
                    {isFirst && (
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />
                    )}
                </div>

                {/* Meta row */}
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--ink)", fontFamily: "var(--font-sans)" }}>
                            {isCurrent ? "Current" : `Save #${index + 1}`}
                        </span>
                        {isCurrent && (
                            <span style={{
                                background: "#dcfce7", color: "#16a34a",
                                borderRadius: 20, padding: "2px 8px",
                                fontSize: 10, fontWeight: 700, fontFamily: "var(--font-mono)",
                            }}>
                                LIVE
                            </span>
                        )}
                        {userName && (
                            <span style={{ fontSize: 11, color: "var(--ink-3)", fontWeight: 600 }}>
                                · {userName}
                            </span>
                        )}
                        {/* <StatusBadge status={entry.status} /> */}
                        <span style={{ fontSize: 10.5, color: "var(--ink-4)", fontFamily: "var(--font-mono)", marginLeft: "auto" }}>
                            {fmt(entry.createdAt || entry.created_at)}
                        </span>
                    </div>
                </div>

                {/* Chevron */}
                <div style={{
                    width: 22, height: 22, borderRadius: "50%",
                    border: "1.5px solid var(--rule)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0, transition: "transform 0.2s",
                    transform: open ? "rotate(180deg)" : "rotate(0deg)",
                    background: open ? "var(--accent-3)" : "transparent",
                }}>
                    <KpiIcon name="chevron" size={11} color={open ? "var(--accent)" : "var(--ink-4)"} />
                </div>
            </div>

            {/* Expanded panel */}
            {open && (
                <div style={{
                    marginLeft: 34, marginBottom: 8,
                    background: "var(--surface)", borderRadius: 10,
                    border: "1px solid var(--rule)", overflow: "hidden",
                }}>
                    {gridData && gridData.length > 0 ? (
                        <TabularDynamicForm
                            question={question}
                            gridData={gridData}
                            setGridData={() => {}}
                            readOnly={true}
                            columnGroups={entry?.columnGroups || null}
                            rowNames={entry?.rowNames || []}
                        />
                    ) : (
                        <div style={{ padding: "16px 20px", fontSize: 12, color: "var(--ink-4)", fontStyle: "italic" }}>
                            No tabular data recorded for this entry.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const QualitativeHistoryModal = ({ show, onHide, question, financialYearId }) => {
    const [history, setHistory] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const overlayRef = useRef(null);

    const usersById = useMemo(() => {
        const map = {};
        (users || []).forEach((u) => { if (u?.id != null) map[String(u.id)] = u; });
        return map;
    }, [users]);

    useEffect(() => {
        if (!show) return;
        setError("");
        setHistory([]);
        setLoading(true);

        const qId = question?.questionId || question?.id;

        Promise.all([
            apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}qualitativeQuestionAnswer`,
                {}, 
                { questionId: qId, financialYearId },
                "GET"
            ).catch(() => null),
            apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}qualitativeQuestionAnswerHistory`,
                {},
                { questionId: qId, financialYearId },
                "GET"
            ).catch(() => null),
            getAllUsers().catch(() => []),
        ]).then(([currentRes, historyRes, allUsers]) => {
            setUsers(Array.isArray(allUsers) ? allUsers : []);
            const historyRows = (historyRes?.isSuccess && Array.isArray(historyRes?.data?.history))
                ? historyRes.data.history
                : [];

            const currentAnswers = currentRes?.isSuccess
                ? normalizeAnswers(currentRes?.data?.answers)
                : [];
            const currentAnswer =
                currentAnswers.find(a => Number(a.questionId || a.question_id) === Number(qId))
                || currentAnswers[0]
                || null;

            const merged = [
                ...(currentAnswer ? [{ ...currentAnswer, _isCurrent: true }] : []),
                ...historyRows,
            ];
            setHistory(merged);
        }).catch(() => {
            setError("Failed to load history.");
        }).finally(() => {
            setLoading(false);
        });
    }, [show, question, financialYearId]);

    useEffect(() => {
        const h = (e) => { if (e.key === "Escape") onHide(); };
        document.addEventListener("keydown", h);
        return () => document.removeEventListener("keydown", h);
    }, [onHide]);

    if (!show) return null;

    const modal = (
        <div
            ref={overlayRef}
            onClick={e => { if (e.target === overlayRef.current) onHide(); }}
            style={{
                position: "fixed", inset: 0,
                background: "rgba(15,23,42,0.45)",
                zIndex: 9999, display: "flex",
                alignItems: "center", justifyContent: "center",
                backdropFilter: "blur(2px)",
            }}
        >
            <div style={{
                background: "var(--surface)",
                borderRadius: 16,
                boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
                width: "100%", maxWidth: 1080,
                maxHeight: "88vh",
                display: "flex", flexDirection: "column",
                margin: "0 16px",
            }}>
                {/* Header */}
                <div style={{
                    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                    padding: "18px 24px", borderBottom: "1px solid #dbe4fd",
                    background: "linear-gradient(135deg,#f8faff 0%,#f1f5fd 100%)",
                    borderRadius: "16px 16px 0 0",
                    flexShrink: 0,
                }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flex: 1, minWidth: 0 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                            background: "var(--accent-3)",
                            border: "1.5px solid var(--accent-2)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <KpiIcon name="history" size={16} color="var(--accent)" />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                                fontSize: 10.5, fontWeight: 700, color: "#3346c2",
                                fontFamily: "var(--font-mono)", textTransform: "uppercase",
                                letterSpacing: "0.09em", marginBottom: 4,
                            }}>
                                Answer History
                            </div>
                            <div style={{
                                fontSize: 15.5, fontWeight: 700, color: "var(--ink)",
                                lineHeight: 1.45, letterSpacing: "-0.01em", wordBreak: "break-word",
                            }}>
                                {question?.title || question?.question_explanation || ""}
                            </div>
                        </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {!loading && history.length > 0 && (
                            <span style={{
                                background: "var(--accent-3)", color: "var(--accent)",
                                padding: "2px 9px", borderRadius: 20,
                                fontSize: 10, fontWeight: 700, fontFamily: "var(--font-mono)",
                            }}>
                                {history.length} {history.length === 1 ? "entry" : "entries"}
                            </span>
                        )}
                        <button onClick={onHide} style={{
                            width: 30, height: 30, borderRadius: "50%",
                            border: "1.5px solid var(--rule)", background: "transparent",
                            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <KpiIcon name="x" size={13} color="var(--ink-3)" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px 20px" }}>
                    {loading ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "center", padding: "40px 0", color: "var(--ink-4)", fontSize: 13 }}>
                            <div style={{ width: 16, height: 16, border: "2px solid var(--rule)", borderTopColor: "var(--accent)", borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                            Loading history…
                        </div>
                    ) : error ? (
                        <div style={{ padding: "20px", textAlign: "center", color: "var(--red)", fontSize: 13, fontWeight: 600 }}>
                            ⚠️ {error}
                        </div>
                    ) : history.length === 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "44px 0", gap: 12, textAlign: "center" }}>
                            <div style={{ width: 44, height: 44, borderRadius: 13, background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <KpiIcon name="history" size={20} color="var(--ink-4)" />
                            </div>
                            <div style={{ fontWeight: 700, fontSize: 14, color: "var(--ink-2)" }}>No history yet</div>
                            <div style={{ fontSize: 12, color: "var(--ink-4)", maxWidth: 260 }}>
                                Every time data is saved for this question, a history entry will appear here.
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div style={{ fontSize: 11, color: "var(--ink-4)", marginBottom: 14, fontFamily: "var(--font-mono)" }}>
                                Newest first · click an entry to expand the data
                            </div>
                            {history.map((entry, i) => (
                                <HistoryEntry
                                    key={entry.id != null ? `${entry._isCurrent ? "cur" : "hist"}-${entry.id}` : i}
                                    entry={entry}
                                    question={question}
                                    index={i}
                                    isLast={i === history.length - 1}
                                    usersById={usersById}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: "14px 24px", borderTop: "1px solid var(--rule)",
                    background: "#fafbfc", borderRadius: "0 0 16px 16px",
                    display: "flex", justifyContent: "flex-end", flexShrink: 0,
                }}>
                    <button
                        onClick={onHide}
                        className="kpi-btn-secondary"
                        style={{ fontSize: 13, padding: "7px 18px" }}
                    >
                        Close
                    </button>
                </div>
            </div>

            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );

    return ReactDOM.createPortal(modal, document.body);
};

export default QualitativeHistoryModal;
